import React, { FC } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { ImageDataLike } from "gatsby-plugin-image";
import { LinkType } from "../../types";
import { createMarkup, useBackgroundColor } from "../../utils";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Image, { StyledImage } from "../image/Image";
import Typography from "../typography/Typography";
import Stack from "../stack/Stack";
import Link from "../link/Link";
import Html from "../html/Html";

// Interface

export interface FullWidthImageProps {
  image?: ImageDataLike | string;
  mobileImage?: ImageDataLike | string;
  heading?: string;
  subHeading?: string;
  link?: LinkType;
  backgroundColor?: ThemeColorsKeys;
  bannerOnRight?: boolean;
  children?: React.ReactNode;
}

interface StyledFullWidthImageOverlayProps {
  backgroundColor: ThemeColorsKeys;
  bannerOnRight?: boolean;
}

interface StyledFullWidthImageContainerProps {
  imageOnly: boolean;
}

interface StyledFullWidthImageProps {
  imageOnly: boolean;
}

interface StyledImageContainerProps {
  hasMobileImage?: boolean;
}

// Styles

const snapPoint = "large";

export const StyledFullWidthImage = styled.div<StyledFullWidthImageProps>`
  ${({ theme: { space, maxMedia }, imageOnly }) => css`
    @media (max-width: ${maxMedia[snapPoint]}) {
      margin: 0 auto;
      padding: ${space.fluidLargeToXxxxxLarge} ${space.small};

      ${imageOnly &&
      css`
        padding: 0;

        * {
          border-radius: 0;
          border: 0;
        }
      `}
    }
  `};
`;

const StyledImageContainer = styled(Image)<StyledImageContainerProps>`
  ${({ theme: { media, maxMedia }, hasMobileImage }) => css`

    @media (max-width: ${maxMedia[snapPoint]}) {
      display: ${hasMobileImage ? "none" : "block"};
    }

    @media (min-width: ${media[snapPoint]}) {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;

      img {
        height: 100%;
        object-fit: cover;
      }
    }
  `};
`;

const StyledMobileImageContainer = styled(Image)`
  ${({ theme: { media } }) => css`
  width:100%;
    @media (min-width: ${media[snapPoint]}) {
      display: none;
    }
  `};
`;

export const StyledFullWidthImageContainer = styled.div<StyledFullWidthImageContainerProps>`
  ${({ theme: { space, media, maxMedia, borderRadii }, imageOnly }) => css`
    position: relative;

    ${!imageOnly &&
    css`
      @media (max-width: ${maxMedia[snapPoint]}) {
        border-radius: ${borderRadii.small};
        border: solid 1px currentColor;
        overflow: hidden;
        isolation: isolate;
      }
    `}

    @media (min-width: ${media.medium}) {
      /* padding-left: ${space.large};
      padding-right: ${space.large}; */
    }

    @media (min-width: ${media[snapPoint]}) {
      padding-top: ${space.xxxxLarge};
      padding-bottom: ${space.xxxxLarge};
      padding-left: ${space.xLarge};
      padding-right: ${space.xLarge};
      min-height: ${imageOnly ? "800px" : "auto"};
    }
  `};
`;

export const StyledFullWidthImageOverlay = styled(Stack)<StyledFullWidthImageOverlayProps>`
  ${({
    theme: { space, media, borderRadii },
    backgroundColor,
    bannerOnRight,
  }) => css`
    ${useBackgroundColor(backgroundColor)}
    padding: ${space.fluidMediumToLarge};
    border-top: solid 1px currentColor;

    @media (min-width: ${media[snapPoint]}) {
      border-radius: ${borderRadii.small};
      border: solid 1px currentColor;
      overflow: hidden;
      isolation: isolate;
      max-width: 608px;

      ${bannerOnRight &&
      css`
        margin-left: auto;
      `}
    }
  `};
`;

const StyledFullWidthImageOverlayContainer = styled.div`
  ${({ theme: { media } }) => css`
    @media (min-width: ${media[snapPoint]}) {
      margin: 0 auto;
      max-width: 1200px;
      z-index: 2;
      position: relative;
    }
  `};
`;

// JSX

export const FullWidthImage: FC<FullWidthImageProps> = ({
  image,
  mobileImage,
  heading,
  subHeading,
  link,
  backgroundColor = "yellow",
  bannerOnRight,
  children,
}) => (
  <StyledFullWidthImage imageOnly={!heading}>
    <StyledFullWidthImageContainer imageOnly={!heading}>
      {mobileImage && <StyledMobileImageContainer src={mobileImage} />}
      {image && <StyledImageContainer src={image} hasMobileImage={mobileImage ? true : false} />}
      {heading && (
        <StyledFullWidthImageOverlayContainer>
          <StyledFullWidthImageOverlay
            gap="fluidSmallToLarge"
            backgroundColor={backgroundColor}
            bannerOnRight={bannerOnRight}
          >
            {heading && (
              <Html>
                {heading}
              </Html>
            )}
            <Stack gap="fluidSmallToMedium">
              {subHeading && (
                <Html>
                  {createMarkup(subHeading)}
                </Html>
              )}
              {children && children}
              {link && (
                <Link
                  {...link}
                  dataLayerClickContext={`Full width image banner - ${heading}`}
                />
              )}
            </Stack>
          </StyledFullWidthImageOverlay>
        </StyledFullWidthImageOverlayContainer>
      )}
    </StyledFullWidthImageContainer>
  </StyledFullWidthImage>
);

export default FullWidthImage;
